import Keycloak from "keycloak-js";
import axios from "axios";
import { store } from "../../client";
import { setLocale } from "../../redux/modules/_BETA/selections";
import { logout, login_keycloak } from "../../redux/modules/auth/actionCreators";
const _kc = new Keycloak("/keycloak.json");
let userAuth = false;

/**
 * Syncs the fresh token with Axios headers and Redux store
 */
const syncTokenState = (token) => {
  if (!token) return;
  axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  const username = _kc.tokenParsed?.preferred_username;
  if (username) {
    store.dispatch(login_keycloak(username, token));
  }
};

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 * Stores the Authentication object in _kc variable where we can access all provided keycloak methods
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback) => {
  // Silent background token refresh callback provided by keycloak-js
  _kc.onTokenExpired = () => {
    _kc
      .updateToken(70)
      .then((refreshed) => {
        if (refreshed) {
          syncTokenState(_kc.token);
        }
      })
      .catch((err) => {
        console.error("Failed to silently refresh token", err);
        doLogout();
      });
  };

  _kc.onAuthRefreshSuccess = () => {
    syncTokenState(_kc.token);
  };

  _kc
    .init({
      // we can define the keycloak server here instead using the keycloak.json file
      // url: "http://apiHost:keycloakPort/auth",
      // realm: "Realm",
      // client_id: "clientId"
      // "check-sso" silently checks for an existing KC session via the iframe at
      // silentCheckSsoRedirectUri before deciding to redirect to login.
      // This allows KC to use the server-side SSO session to silently restore tokens
      // without an immediate hard redirect to the login page.
      //
      // silentCheckSsoRedirectUri makes keycloak-js run a 3rd-party-cookie iframe
      // check against the Keycloak server's origin (regardless of checkLoginIframe).
      // In local dev the app (localhost) and Keycloak (a remote host) are on
      // different origins, so that iframe gets blocked by X-Frame-Options and the
      // whole init() call times out and rejects -- leaving a blank page. In any
      // real deployment the app and Keycloak share an origin, so this only needs
      // to be disabled for local development.
      onLoad: "check-sso",
      ...(process.env.NODE_ENV === "development"
        ? {}
        : {
            silentCheckSsoRedirectUri:
              window.location.origin + "/silent-check-sso.html",
          }),
      pkceMethod: "S256",
      checkLoginIframe: false,
    })
    .then((authenticated) => {
      userAuth = true;
      if (!authenticated) {
        doLogin();
        return;
      }

      syncTokenState(_kc.token);

      _kc
        .loadUserProfile()
        .then((profile) => {
          const locale = profile?.attributes?.["locale"];
          if (locale !== undefined) {
            store.dispatch(setLocale(locale));
          }
        })
        .catch(console.error);

      onAuthenticatedCallback();
    })
    .catch(err => console.error(err));
};

const doLogin = () => _kc.login();

const doLogout = () => {
  userAuth = false;
  delete axios.defaults.headers.common["Authorization"];
  _kc.logout({ redirectUri: window.location.origin });
};

const getToken = () => _kc.token;
const getTokenParsed = () => _kc.tokenParsed;
const isLoggedIn = () => !!_kc.token;

/**
 * Updates token if it is expiring within minValidity seconds (default 70s).
 * Returns a promise resolving to the token string.
 */
const updateToken = (minValidity = 70) => {
  if (_kc.sessionId && userAuth) {
    return _kc
      .updateToken(minValidity)
      .then((refreshed) => {
        if (refreshed) {
          syncTokenState(_kc.token);
        }
        return _kc.token;
      })
      .catch((err) => {
        console.error("Token update failed", err);
        doLogout();
        return null;
      });
  }
  return Promise.resolve(_kc.token);
};

const getUsername = () => _kc.tokenParsed?.preferred_username;
const hasRole = (roles) => roles.some((role) => _kc.hasRealmRole(role));
const getEmail = () => _kc.tokenParsed?.email;
const clearToken = _kc.clearToken;
const isTokenExpired = (minValidity = 5) => _kc.isTokenExpired(minValidity);
const isUserAuthenticated = () => userAuth;

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isLoggedIn,
  getToken,
  updateToken,
  getUsername,
  hasRole,
  getTokenParsed,
  getEmail,
  clearToken,
  isTokenExpired,
  isUserAuthenticated,
};

export default UserService;

